import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { AdminLayout } from '@/components/admin/Layout';
import { Toaster } from 'sonner';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import SalesStaffPage from '@/pages/SalesStaffPage';
import CustomersPage from '@/pages/CustomersPage';
import ProductsPage from '@/pages/ProductsPage';
import PricingCostingPage from '@/pages/PricingCostingPage';
import PricingHistoryPage from '@/pages/PricingHistoryPage';
import WarehousePage from '@/pages/WarehousePage';
import WarehouseOverviewPage from '@/pages/WarehouseOverviewPage';
import StockRegisterPage from '@/pages/StockRegisterPage';
import WarehousesPage from '@/pages/WarehousesPage';
import WarehouseStockDetailsPage from '@/pages/WarehouseStockDetailsPage';
import RawMaterialsPage from '@/pages/RawMaterialsPage';
import PackingMaterialsPage from '@/pages/PackingMaterialsPage';
import RawStockPage from '@/pages/RawStockPage';
import RawStockAuditPage from '@/pages/RawStockAuditPage';
import PackingRequestsPage from '@/pages/PackingRequestsPage';
import PackingSessionsPage from '@/pages/PackingSessionsPage';
import BlendOperationsPage from '@/pages/BlendOperationsPage';
import VendorsPage from '@/pages/VendorsPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import VendorPaymentsPage from '@/pages/VendorPaymentsPage';
import OrdersPage from '@/pages/OrdersPage';
import PaymentsPage from '@/pages/PaymentsPage';
import StaffCollectionsPage from '@/pages/StaffCollectionsPage';
import FinancialYearClosePage from '@/pages/FinancialYearClosePage';
import LeadsPage from '@/pages/LeadsPage';
import LeadTasksPage from '@/pages/LeadTasksPage';
import TasksPage from '@/pages/TasksPage';
import CrmPage from '@/pages/CrmPage';
import StaffActivitiesPage from '@/pages/StaffActivitiesPage';
import PayrollPage from '@/pages/PayrollPage';
import ReportsPage from '@/pages/ReportsPage';
import ReportsSalesPage from '@/pages/reports/ReportsSalesPage';
import ReportsStaffPage from '@/pages/reports/ReportsStaffPage';
import ReportsProductsPage from '@/pages/reports/ReportsProductsPage';
import ReportsCollectionsPage from '@/pages/reports/ReportsCollectionsPage';
import ReportsFinancialPage from '@/pages/reports/ReportsFinancialPage';
import ReportsShortUrlsPage from '@/pages/reports/ReportsShortUrlsPage';
import ReportsQrCodesPage from '@/pages/reports/ReportsQrCodesPage';
import ReportsAlertsPage from '@/pages/reports/ReportsAlertsPage';
import SettingsPage from '@/pages/SettingsPage';
import PushNotificationsPage from '@/pages/PushNotificationsPage';
import SecurityAuditPage from '@/pages/SecurityAuditPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import ShortUrlsSettingsPage from '@/pages/settings/ShortUrlsSettingsPage';
import QrCodesSettingsPage from '@/pages/settings/QrCodesSettingsPage';
import TestimonialsSettingsPage from '@/pages/settings/TestimonialsSettingsPage';
import ExpensesPage from '@/pages/ExpensesPage';
import VouchersPage from '@/pages/VouchersPage';
import CreditNotesPage from '@/pages/CreditNotesPage';
import LedgerPage from '@/pages/LedgerPage';
import SalesReturnsPage from '@/pages/SalesReturnsPage';
import GstPage from '@/pages/GstPage';

import '@/App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected Admin Routes */}
          <Route element={<AdminLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/sales-staff" element={<SalesStaffPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/pricing-costing" element={<PricingCostingPage />} />
            <Route path="/products/:variantId/pricing" element={<PricingCostingPage />} />
            <Route path="/products/:variantId/pricing-history" element={<PricingHistoryPage />} />
            <Route path="/warehouse" element={<WarehousePage />} />
            <Route path="/warehouse/overview" element={<WarehouseOverviewPage />} />
            <Route path="/warehouse/list" element={<WarehousesPage />} />
            <Route path="/warehouse/stock-details" element={<WarehouseStockDetailsPage />} />
            <Route path="/warehouse/raw-materials" element={<RawMaterialsPage />} />
            <Route path="/warehouse/packing-materials" element={<PackingMaterialsPage />} />
            <Route path="/warehouse/raw-stock" element={<RawStockPage />} />
            <Route path="/warehouse/raw-stock-audit" element={<RawStockAuditPage />} />
            <Route path="/warehouse/packing-sessions" element={<PackingSessionsPage />} />
            <Route path="/warehouse/blending" element={<BlendOperationsPage />} />
            <Route path="/warehouse/packing-requests" element={<PackingRequestsPage />} />
            <Route path="/warehouse/stock-register" element={<StockRegisterPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/vendor-payments" element={<VendorPaymentsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/staff-collections" element={<StaffCollectionsPage />} />
            <Route path="/financial-year-close" element={<FinancialYearClosePage />} />
            <Route path="/crm" element={<CrmPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/lead-tasks" element={<LeadTasksPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/staff-activities" element={<StaffActivitiesPage />} />
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/vouchers" element={<VouchersPage />} />
            <Route path="/credit-notes" element={<CreditNotesPage />} />
            <Route path="/sales-returns" element={<SalesReturnsPage />} />
            <Route path="/ledger" element={<LedgerPage />} />
            <Route path="/gst" element={<GstPage />} />
            <Route path="/reports" element={<Navigate to="/reports/sales" replace />} />
            <Route path="/reports/sales" element={<ReportsSalesPage />} />
            <Route path="/reports/staff" element={<ReportsStaffPage />} />
            <Route path="/reports/products" element={<ReportsProductsPage />} />
            <Route path="/reports/collections" element={<ReportsCollectionsPage />} />
            <Route path="/reports/financial" element={<ReportsFinancialPage />} />
            <Route path="/reports/alerts" element={<ReportsAlertsPage />} />
            <Route path="/reports/short-urls" element={<ReportsShortUrlsPage />} />
            <Route path="/reports/qr-codes" element={<ReportsQrCodesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/push-notifications" element={<PushNotificationsPage />} />
            <Route path="/security/audit-logs" element={<SecurityAuditPage />} />
            <Route path="/settings/short-urls" element={<ShortUrlsSettingsPage />} />
            <Route path="/settings/qr-codes" element={<QrCodesSettingsPage />} />
            <Route path="/settings/testimonials" element={<TestimonialsSettingsPage />} />
            <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
            <Route path="/email-templates" element={<Navigate to="/settings/email-templates" replace />} />
          </Route>
          
          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={{
          classNames: {
            toast: 'agniya-toast',
            title: 'agniya-toast-title',
            description: 'agniya-toast-description',
            success: 'agniya-toast-success',
            error: 'agniya-toast-error',
            warning: 'agniya-toast-warning',
            info: 'agniya-toast-info',
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
